import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const MIN_LEN = 10;

// Shared by the agency Account page and the driver portal.
export default function ChangePasswordForm({ compact = false }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < MIN_LEN) return setErr(`New password must be at least ${MIN_LEN} characters.`);
    if (!/[A-Za-z]/.test(next) || !/[0-9]/.test(next)) return setErr("New password must include at least one letter and one number.");
    if (next !== confirm) return setErr("The two new passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      // Older sessions are now invalid; keep this one alive with the fresh token
      if (data?.access_token) localStorage.setItem("nemt_token", data.access_token);
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password updated");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300";
  const label = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-4 max-w-md"} data-testid="change-password-form">
      <div>
        <label className={label}>Current password</label>
        <input data-testid="cp-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={input} required />
      </div>
      <div>
        <label className={label}>New password</label>
        <input data-testid="cp-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={input} required />
        <div className="text-xs text-slate-500 mt-1">At least {MIN_LEN} characters, with a letter and a number.</div>
      </div>
      <div>
        <label className={label}>Confirm new password</label>
        <input data-testid="cp-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} required />
      </div>
      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="cp-error">{err}</div>
      )}
      <button type="submit" disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-60" data-testid="cp-submit">
        <KeyRound size={14} /> {busy ? "Saving..." : "Update password"}
      </button>
    </form>
  );
}
